import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import axiosInstance from 'utils/axiosUtil';
import { MESSAGE_TYPE, AUDIENCE_TYPE } from 'doctivity-shared/utils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
  Typography,
  Box,
  Link,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { PREVIEW_STORAGE_KEY } from 'containers/AnnouncementPreviewRoot';
import { UserPickerDialog } from './UserPickerDialog';

const toDate = (v) => (v ? new Date(v) : null);
const userChipLabel = (u) => `${u.last_name || ''}, ${u.first_name || ''}`.replace(/^, |, $/g, '').trim();

const emptyState = {
  audience: 'system', // 'system' (everyone) | 'users' (specific recipients)
  user_ids: [], // array of user option objects
  title: '',
  body_markdown: '',
  starts_at: null,
  ends_at: null,
  is_active: true,
};

/**
 * Create / edit dialog for an announcement (a Message of type 'announcement').
 * Talks to /api/v2/messages directly. On create, the chosen audience (everyone /
 * clients / users) is resolved server-side into per-user recipient rows.
 * The audience is a send-time snapshot, so it's read-only when editing.
 */
const AnnouncementDialog = ({ open, editing, clients, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setForm({
        ...emptyState,
        title: editing.title || '',
        body_markdown: editing.body_markdown || '',
        starts_at: toDate(editing.starts_at),
        ends_at: toDate(editing.ends_at),
        is_active: editing.is_active !== false,
      });
    } else {
      setForm(emptyState);
    }
  }, [open, editing]);

  const validate = () => {
    if (!form.title.trim()) return 'Title is required.';
    if (!form.body_markdown.trim()) return 'Message body is required.';
    if (!editing) {
      if (form.audience === 'users' && form.user_ids.length === 0) return 'Please choose at least one recipient.';
    }
    if (form.starts_at && form.ends_at && form.ends_at <= form.starts_at) return 'End date must be after the start date.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      type: MESSAGE_TYPE.ANNOUNCEMENT,
      audience_type: form.audience === 'users' ? AUDIENCE_TYPE.USERS : AUDIENCE_TYPE.SYSTEM,
      user_ids: form.audience === 'users' ? form.user_ids.map((u) => u.id) : undefined,
      title: form.title.trim(),
      body_markdown: form.body_markdown.trim(),
      starts_at: form.starts_at ? form.starts_at.toISOString() : null,
      ends_at: form.ends_at ? form.ends_at.toISOString() : null,
      is_active: form.is_active,
    };
    try {
      if (editing) {
        await axiosInstance.put(`/v2/messages/${editing.id}`, payload);
      } else {
        await axiosInstance.post('/v2/messages', payload);
      }
      setSaving(false);
      onSaved();
    } catch (e) {
      setSaving(false);
      setError(e.response?.data?.error || 'Could not save the announcement.');
    }
  };

  // Hand the current draft to a standalone preview tab via localStorage (it isn't
  // saved yet). Reusing one named tab + a timestamp forces it to reload the draft.
  const openPreview = () => {
    const draft = { title: form.title, body_markdown: form.body_markdown };
    try {
      window.localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      /* ignore storage errors */
    }
    window.open(`/announcement-preview?t=${Date.now()}`, 'doctivityAnnouncementPreview');
  };

  const recipientCount = form.user_ids.length;
  const recipientSummary =
    recipientCount === 0
      ? 'No recipients selected yet.'
      : `${recipientCount} recipient${recipientCount === 1 ? '' : 's'} selected: `
        + `${form.user_ids.slice(0, 3).map(userChipLabel).join(', ')}`
        + `${recipientCount > 3 ? ` +${recipientCount - 3} more` : ''}`;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
        <DialogTitle>{editing ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
        <DialogContent dividers>
          {/* Audience — chosen once and fixed at send time, so read-only when editing. */}
          {editing ? (
            <Box sx={{ mb: 2 }}>
              <FormLabel>Audience</FormLabel>
              <Typography>{editing.audience || 'All recipients'}</Typography>
              <Typography variant='caption' color='textSecondary'>
                Recipients are set when an announcement is sent and can&apos;t be changed by editing.
              </Typography>
            </Box>
          ) : (
            <FormControl component='fieldset' fullWidth sx={{ mb: 2 }}>
              <FormLabel>Audience</FormLabel>
              <RadioGroup row value={form.audience} onChange={(e) => set({ audience: e.target.value })}>
                <FormControlLabel value='system' control={<Radio />} label='Everyone' />
                <FormControlLabel value='users' control={<Radio />} label='Specific recipients' />
              </RadioGroup>
              {form.audience === 'users' && (
                <Box sx={{ mt: 1 }}>
                  <Button variant='outlined' size='small' onClick={() => setUserPickerOpen(true)}>
                    {form.user_ids.length ? 'Edit recipients…' : 'Select recipients…'}
                  </Button>
                  <Typography variant='caption' color='textSecondary' sx={{ display: 'block', mt: 0.5 }}>
                    {recipientSummary}
                  </Typography>
                </Box>
              )}
            </FormControl>
          )}

          {/* Title */}
          <TextField
            label='Title'
            fullWidth
            required
            value={form.title}
            inputProps={{ maxLength: 255 }}
            onChange={(e) => set({ title: e.target.value })}
            sx={{ mb: 2 }}
          />

          {/* Body (Markdown) */}
          <TextField
            label='Message (Markdown supported)'
            fullWidth
            required
            multiline
            minRows={4}
            value={form.body_markdown}
            onChange={(e) => set({ body_markdown: e.target.value })}
          />
          <Typography variant='caption' color='textSecondary' sx={{ display: 'block', mt: 0.5 }}>
            Supports Markdown (
            <Link href='/markdown-tips' target='_blank' rel='noopener noreferrer'>Markdown Tips and Tricks</Link>
            {' - opens in a new tab)'}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Link component='button' type='button' variant='caption' onClick={openPreview}>
              Preview Announcement
            </Link>
          </Box>

          {/* No call-to-action field: an announcement often covers several things, so
              one button at the bottom can't say where it goes. Links belong in the body
              as Markdown — [text](/providers/123) — next to the sentence that explains
              them. The Markdown Tips link above covers the syntax. */}

          {/* Schedule */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <DatePicker
              label='Start date (optional)'
              value={form.starts_at}
              onChange={(v) => set({ starts_at: v })}
              maxDate={form.ends_at || undefined}
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
            <DatePicker
              label='End date (optional)'
              value={form.ends_at}
              onChange={(v) => set({ ends_at: v })}
              minDate={form.starts_at || undefined}
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
          </Box>

          {/* Active */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />}
            label='Active (visible to users)'
          />

          {error && (
            <Typography color='error' sx={{ mt: 1 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color='primary'>Cancel</Button>
          <Button onClick={handleSave} variant='contained' color='primary' disabled={saving}>
            {editing ? 'Save' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>
      <UserPickerDialog
        open={userPickerOpen}
        initialSelected={form.user_ids}
        clients={clients}
        onClose={() => setUserPickerOpen(false)}
        onConfirm={(sel) => {
          set({ user_ids: sel });
          setUserPickerOpen(false);
        }}
      />
    </>
  );
};

AnnouncementDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  editing: PropTypes.object,
  clients: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export { AnnouncementDialog };
export default AnnouncementDialog;
